import { type HighlightsRenderer } from '@databases/highlights';

// Load the drawable presence layer's styles as a side-effect when this module
// is imported. The renderer is the sole consumer of these rules, so they live
// with it instead of in `@databases/highlights`.
import '../styles/index.ts';
import { type PresenceLayerGroupSnapshot } from '../presence-layer-state.ts';
import {
  PRESENCE_BOX_CLASS,
  PRESENCE_BOX_HOVERED_CLASS,
  PRESENCE_NAMEPLATE_CLASS
} from '../styles/constants.ts';

import { buildNameplateText } from './build-nameplate-text.ts';
import { PRESENCE_LAYER_Z_INDEX } from './constants.ts';
import {
  type FormatNameplateOverflow,
  type PresenceLayerRendererDeps,
  type PresenceViewAdapter
} from './types.ts';

interface Placement {
  id: string;
  row: number;
  /**
   * Inclusive column span. Cell presence spans a single column; row presence
   * spans the whole data area (col 0..last) so its box sits inside the grid's
   * sticky entry-number gutter instead of being clipped underneath it.
   */
  anchorCol: number;
  leadCol: number;
}

/**
 * Resolves a presence group to its grid placement, or `null` when it should not
 * be drawn by this layer (column-only presence, or a field/entry not currently
 * visible). Cell presence needs both a visible field and entry; row presence
 * needs only a visible entry and at least one visible column to span.
 */
function resolvePlacement(
  group: PresenceLayerGroupSnapshot,
  adapter: PresenceViewAdapter
): Placement | null {
  if (group.entryId === null) {
    return null;
  }

  const row = adapter.getVisibleEntryIds().indexOf(group.entryId);

  if (row === -1) {
    return null;
  }

  if (group.fieldId === null) {
    const lastCol = adapter.getVisibleFieldIds().length - 1;

    if (lastCol < 0) {
      return null;
    }

    return { id: `row:${group.entryId}`, row, anchorCol: 0, leadCol: lastCol };
  }

  const col = adapter.getVisibleFieldIds().indexOf(group.fieldId);

  if (col === -1) {
    return null;
  }

  return {
    id: `cell:${group.fieldId}:${group.entryId}`,
    row,
    anchorCol: col,
    leadCol: col
  };
}

/**
 * Appends the nameplate child (collaborator names) to a presence box element,
 * unless there are no names to show.
 */
function appendNameplate(
  el: HTMLElement,
  group: PresenceLayerGroupSnapshot,
  formatOverflow: FormatNameplateOverflow
): () => void {
  const names = group.participants
    .map((participant) => participant.displayName)
    .filter((name): name is string => name !== undefined);

  const text = buildNameplateText(names, formatOverflow);

  if (text === '') {
    return () => {};
  }

  const nameplate = document.createElement('div');
  nameplate.className = PRESENCE_NAMEPLATE_CLASS;
  nameplate.textContent = text;
  el.appendChild(nameplate);

  return () => nameplate.remove();
}

/**
 * Toggles the hovered modifier on each box based on whether the pointer is
 * inside its on-screen rect. The box keeps `pointer-events: none` so the grid
 * stays clickable (selection hit-tests via `elementFromPoint`), which means
 * hover can't be a CSS `:hover`; we hit-test in JS instead.
 */
function applyHover(
  boxes: HTMLElement[],
  pointer: { x: number; y: number } | null
): void {
  for (const box of boxes) {
    const rect = box.getBoundingClientRect();
    const hovered =
      pointer !== null &&
      pointer.x >= rect.left &&
      pointer.x <= rect.right &&
      pointer.y >= rect.top &&
      pointer.y <= rect.bottom;

    box.classList.toggle(PRESENCE_BOX_HOVERED_CLASS, hovered);
  }
}

/**
 * The drawable presence layer. This module owns one `HighlightsController`
 * layer and draws all remote collaborator boxes (cells + rows) plus their
 * nameplates as plain DOM.
 *
 * It is driven by the injected `presence` store: `start` subscribes and redraws
 * the whole layer on every presence change (clear + add), coalescing bursts
 * into one animation frame. The controller's own `ResizeObserver` keeps boxes
 * aligned on column resize, and the overlay scrolls natively with the grid, so
 * no scroll handling is required. Nameplates are hidden until the pointer hovers
 * a box; hover is tracked from `pointermove` and reflected on the box's class.
 */
export function createPresenceLayerRenderer({
  controller,
  presence,
  adapter,
  formatOverflow
}: PresenceLayerRendererDeps): { start(): void; stop(): void } {
  let layer: HighlightsRenderer | null = null;
  let unsubscribe: (() => void) | null = null;
  let rafId: number | null = null;
  let hoverRafId: number | null = null;
  let pointer: { x: number; y: number } | null = null;
  let boxes: HTMLElement[] = [];

  function draw(): void {
    if (!layer && controller.getScrollContainer()) {
      layer = controller.createLayer();
    }

    if (!layer) {
      return;
    }

    layer.clear();
    boxes = [];

    for (const group of presence.getGroups()) {
      const placement = resolvePlacement(group, adapter);

      if (!placement) {
        continue;
      }

      // Palette hue lives at index 3 of `color.background.accent.<hue>.bolder`.
      const hue = group.participants[0].colorToken.split('.')[3];

      const handle = layer.add({
        range: {
          id: placement.id,
          anchor: { row: placement.row, col: placement.anchorCol },
          lead: { row: placement.row, col: placement.leadCol }
        },
        mode: 'cell',
        zIndex: PRESENCE_LAYER_Z_INDEX,
        className: `${PRESENCE_BOX_CLASS} ${PRESENCE_BOX_CLASS}--${hue}`
      });

      handle.bindElements(({ element, role }) => {
        boxes.push(element);
        applyHover([element], pointer);
        const removeNameplate =
          role === 'primary' ? appendNameplate(element, group, formatOverflow) : () => {};

        return () => {
          removeNameplate();
          boxes = boxes.filter((box) => box !== element);
        };
      });
    }

    applyHover(boxes, pointer);
  }

  function scheduleDraw(): void {
    if (rafId !== null) {
      return;
    }

    rafId = requestAnimationFrame(() => {
      rafId = null;
      draw();
    });
  }

  function scheduleHover(): void {
    if (hoverRafId !== null) {
      return;
    }

    hoverRafId = requestAnimationFrame(() => {
      hoverRafId = null;
      applyHover(boxes, pointer);
    });
  }

  function handlePointerMove(event: PointerEvent): void {
    pointer = { x: event.clientX, y: event.clientY };
    scheduleHover();
  }

  return {
    start() {
      unsubscribe = presence.subscribe(scheduleDraw);
      document.addEventListener('pointermove', handlePointerMove);
      draw();
    },

    stop() {
      unsubscribe?.();
      unsubscribe = null;

      document.removeEventListener('pointermove', handlePointerMove);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (hoverRafId !== null) {
        cancelAnimationFrame(hoverRafId);
        hoverRafId = null;
      }

      pointer = null;
      boxes = [];

      if (layer) {
        layer.dispose();
        layer = null;
      }
    }
  };
}
